import * as amplitude from "@amplitude/analytics-browser";
import { untrack } from "solid-js";
import { config } from "~/js/network/network-handler";
import { addPrefixToKeys } from "~/js/utilities/addPrefixToKeys";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { sessionCounter } from "~/solidJs/shared";
import { ExperimentsMethods } from "~/solidJs/shared/helpers/amplitudeExperiments";
import { getWorkspaceEventParams } from "~/solidJs/shared/helpers/amplitudeHelpers";
import {
	getNonDisabledUserFields,
	rememberDangerousUser
} from "~/solidJs/shared/helpers/userHelpers";

const STATUS_MAP = {
	1: "info",
	2: "warning",
	3: "error"
};
export const initAmpli = () => {
	const inited = amplitude.init(
		config.IS_PRODUCTION === "true"
			? "1e78c568cf596345c5e31477a5f0b412"
			: "7423808ba6298833c7ea230333182955",
		{
			minIdLength: 1,
			autocapture: {
				attribution: true,
				pageViews: true,
				sessions: true,
				formInteractions: true,
				fileDownloads: true,
				elementInteractions: false
			}
		}
	);
	ExperimentsMethods.initAmplitudeExperiment();
	return inited.promise;
};

export const addSessionPlugin = async () => {
	const { sessionReplayPlugin } = await import("@amplitude/plugin-session-replay-browser");
	const sessionReplayTracking = sessionReplayPlugin({
		sampleRate: 10
	});

	amplitude.add(sessionReplayTracking);
};

let selectedTemplateId: undefined | number;

export function setSelectedTemplateId(id: number | undefined) {
	selectedTemplateId = id;
}

export const getGaClientId = () => {
	return new Promise<number>((res, rej) => {
		try {
			setTimeout(() => rej(new Error("get client id timeout")), 500);
			// @ts-expect-error
			gtag("get", "G-0YK5T6L0SR", "client_id", (clientId: number) => {
				res(clientId);
			});
		} catch (e) {
			console.error(e);
			rej(e);
		}
	});
};
export const setUserGroup = () => {
	amplitude.setGroup("projectVersion", config.PROJECT_VERSION);
};
const instanceNameAcordPath: Record<string, string> = {
	"/train-panel/": "train-panel",
	"/train-panel/canvas": "canvas"
};
const googleMetricEvent = async (
	goalName: string,
	category: string,
	params: Record<string, number | boolean | string | undefined | null> = {}
) => {
	if (config.IS_PRODUCTION !== "true") {
		console.log(`%c[Google Event] ${goalName}`, "params: ", params, "color: #bbbbbb");
	}
	const user = untrack(getUserStore);
	const prefixedUser = user ? addPrefixToKeys(getNonDisabledUserFields(user), "user_") : null;
	const sessionNumber = sessionCounter.getCurSessionCount();
	const eventParams = {
		sessionCount: sessionNumber,
		event_category: category,
		build_name: config.GRAYLOG_BUILD_NAME,
		project_version: config.PROJECT_VERSION,
		isCanary: config.FRONT_CANARY,
		eventSource: instanceNameAcordPath[window.location.pathname] || "workspace",
		isMobile: config.IS_MOBILE,
		browser: navigator.userAgent,
		...(selectedTemplateId ? { selectedTemplateId } : {}),
		...(prefixedUser ? prefixedUser : {}),
		...params
	};

	amplitude
		.track(goalName, eventParams, {
			user_id: eventParams.user_id
		})
		.promise.then((val) => {
			if (val.code === 200) return;
			EventAnalytics.sendErrorInfo(
				"amplitude-track-error",
				{
					eventName: val.event.event_type,
					code: val?.code,
					message: val?.message
				},
				false
			);
		});
	if (config.IS_PRODUCTION !== "true") return;
	// @ts-expect-error
	gtag("event", goalName, eventParams);
};
type errorParams = {
	message: string;
	statusCode?: number;
	level?: IGrayLogLevels;
} & {
	[key: string]: string | number | boolean;
};

type IGrayLogLevels = keyof typeof STATUS_MAP;

// Subscription-related event types
type SubscriptionAction =
	| "change_plan"
	| "upgrade"
	| "continue_benefits"
	| "keep_benefits"
	| "downgrade"
	| "update_params"
	| "renew"
	| "cancel"
	| "trial_start"
	| "trial_end"
	| "apply_discount"
	| "remove_discount"
	| "update_billing"
	| "enable_auto_renew"
	| "disable_auto_renew"
	| "update_payment_method"
	| "manage"
	| "change";

type AccountType = "personal" | "team";

type SubscriptionSource = "dropdown" | "header" | "overview" | "subscription";

type IGraylogEventProp = {
	level: IGrayLogLevels;
	message: string;
	log_name: string;
	isNeedToSendToAnalitics: boolean;
} & Record<string, string | number>;

const grayLogEvent = async (props: IGraylogEventProp) => {
	const { level, message, isNeedToSendToAnalitics, ...rest } = props;
	const user = rememberDangerousUser(untrack(getUserStore) || undefined);
	const userInfo = user ? addPrefixToKeys(getNonDisabledUserFields(user), "user_") : {};
	let networkInfo = {};
	const navigator1 = navigator as Record<string, any>;

	if ("connection" in navigator1) {
		networkInfo = addPrefixToKeys(
			{
				downlink: navigator1.connection?.downlink,
				effectiveType: navigator1.connection?.effectiveType,
				rtt: navigator1.connection?.rtt,
				saveData: navigator1.connection?.saveData
			},
			"connection_"
		);
	}
	const bodyData = {
		build_name: config.GRAYLOG_BUILD_NAME,
		level: level,
		message: message ? message : rest?.log_name,
		project_name: "phygital-plus-web-editor",
		project_version: config.PROJECT_VERSION,
		host: window.location.origin,
		browser: navigator.userAgent,
		...userInfo,
		...rest,
		...networkInfo
	};

	fetch(config.GRAYLOG_HTTP_HOST, {
		method: "POST",
		headers: {
			"Content-Type": "application/json"
		},
		body: JSON.stringify(bodyData)
	});
	if (isNeedToSendToAnalitics) {
		googleMetricEvent(props.log_name, STATUS_MAP[level], bodyData);
	}
};
type sendErrorLogProps = {
	message: string;
	level?: IGrayLogLevels;
	log_name: string;
	isNeedToSendToAnalitics: boolean;
} & {
	[key: string]: string | number | boolean;
};
export const GrayLog = {
	sendErrorLog: (prop: sendErrorLogProps) => {
		const { level, ...rest } = prop;
		const obj = {
			...rest,
			level: level || 3
		} as IGraylogEventProp;

		grayLogEvent(obj);
	},
	debug: (prop: sendErrorLogProps) => {
		const { level, ...rest } = prop;
		const obj = {
			...rest,
			level: level || 7
		} as IGraylogEventProp;

		grayLogEvent(obj);
	}
};

/**
 * Analytics events
 */
export const EventAnalytics = {
	mobileContinueAnywayClick: () =>
		googleMetricEvent("mobile-continue-anyway-click", "user-interaction"),
	stylesTrainBtnClick: () => googleMetricEvent("styles-train-btn-click", "user-interaction"),
	tryAddFile: () => googleMetricEvent("try-add-file", "user-interaction"),
	addFile: () => googleMetricEvent("add-file", "user-interaction"),
	manageSubscriptionModalBtnClick: () =>
		googleMetricEvent("manage-subscription-modal-btn-click", "user-interaction"),
	headerManageSubscriptionModalBtnClick: () =>
		googleMetricEvent("header-manage-subscription-modal-btn-click", "user-interaction"),
	newYearBtnClick: (id: string) =>
		googleMetricEvent("new-year-btn-click", "user-interaction", { id }),
	blackFridayClick: () => googleMetricEvent("black-friday-click", "user-interaction"),
	useMobileOfferBtnClick: () =>
		googleMetricEvent("use-mobile-offer-btn-click", "user-interaction"),
	termsLinkClick: () => googleMetricEvent("terms-link-click", "user-interaction"),
	/**
	 * node events
	 */
	initNodeExecution: () => googleMetricEvent("init-node-execution", "user-interaction"),
	initNodeParamChange: () => googleMetricEvent("init-node-param-change", "user-interaction"),
	initNodeInputChange: () => googleMetricEvent("init-node-input-change", "user-interaction"),
	canvasHeaderBtnClick: () => googleMetricEvent("canvas-header-btn-click", "user-interaction"),
	trackTaskStateChanges: (props: Record<string, any>) =>
		googleMetricEvent("task-state-changes", "user-interaction", props),
	sidebarSearch: (searchSate: string) =>
		googleMetricEvent("sidebar-search", "user-interaction", { searchSate }),
	sidebarScroll: (position: number) =>
		googleMetricEvent("sidebar-scroll", "user-interaction", { position }),
	sidebarCollapseGroup: (parentCategory: string, groupName: string, isCollapsed: boolean) =>
		googleMetricEvent("sidebar-collapse-group", "user-interaction", {
			parentCategory,
			groupName,
			isCollapsed
		}),

	halloweenBanner: {
		openHalloweenBanner: () => googleMetricEvent("halloween-open", "user-interaction"),
		copyHalloweenCode: () => googleMetricEvent("halloween-copy", "user-interaction"),
		openPremiumBanner: () => googleMetricEvent("halloween-premium-open", "user-interaction")
	},
	nodeActions: {
		copyNode: () => googleMetricEvent("node-copy-clipboard", "user-interaction"),
		pasteNode: () => googleMetricEvent("node-paste-clipboard", "user-interaction")
	},
	nodeContextMenu: {
		/**
		 * Track when node context menu is opened
		 */
		openContextMenu: (nodeGlobalId: string, position: { x: number; y: number }) =>
			googleMetricEvent("node-context-menu-open", "user-interaction", {
				nodeGlobalId,
				positionX: position.x,
				positionY: position.y
			}),

		/**
		 * Track unified context menu action with details about what user selected
		 */
		contextMenuAction: (action: string, nodeGlobalId: string) =>
			googleMetricEvent("node-context-menu-action", "user-interaction", {
				action,
				nodeGlobalId
			})
	},
	addNodeViaLibrary: (globalId: string, nodesAmount: number) =>
		googleMetricEvent("add-node-via-library", "user-interaction", {
			globalId,
			nodesAmount
		}),
	addNodeViaContextMenu: (globalId: string, nodesAmount: number) =>
		googleMetricEvent("add-node-via-context", "user-interaction", {
			globalId,
			nodesAmount
		}),
	submitAuthForm: (formName: string, email?: string) =>
		googleMetricEvent("submit-auth-form", "user-interaction", {
			formName,
			...(email ? { email } : {})
		}),
	submitAuthFormClick: (formName: string) =>
		googleMetricEvent("submit-auth-form-click", "user-interaction", {
			formName
		}),
	verifyEmailPageBackToLogin: () =>
		googleMetricEvent("verify-email-page-back-to-login", "user-interaction"),
	verifyEmailVisit: () => googleMetricEvent("visit-verify-email-page", "user-interaction"),
	addNodeViaLexica: (prompt: string) =>
		googleMetricEvent("add-node-via-lexica", "user-interaction", {
			prompt
		}),
	deleteSticker: (imageSrc?: string) =>
		googleMetricEvent("delete-sticker", "user-interaction", {
			...(imageSrc ? { imageSrc } : {})
		}),
	moveSticker: (imageSrc?: string) =>
		googleMetricEvent("move-sticker", "user-interaction", {
			...(imageSrc ? { imageSrc } : {})
		}),
	loadInitTemplate: (templateId: number) =>
		googleMetricEvent("load-init-template", "user-interaction", {
			templateId
		}),
	homePage: {
		leftSidebarItemClick: (source: string, name: string) =>
			googleMetricEvent("left-sidebar-item-click", "user-interaction", {
				source,
				name
			}),

		createNewProject: () =>
			googleMetricEvent("home-page-create-new-project", "user-interaction", {}),
		startCreatingClick: (name: string) =>
			googleMetricEvent("home-page-start-creating-click", "user-interaction", {
				name
			}),
		templatesClick: (name: string) =>
			googleMetricEvent("home-page-templates-click", "user-interaction", {
				name
			}),
		projectClick: (id: number) =>
			googleMetricEvent("home-page-prject-click", "user-interaction", {
				id
			}),
		galleryClick: (name: string) =>
			googleMetricEvent("home-page-gallery-click", "user-interaction", {
				name
			}),
		aiAppsClick: (name: string) =>
			googleMetricEvent("home-page-ai-apps-click", "user-interaction", {
				name
			})
	},
	manageWorkspaces: () => googleMetricEvent("manage-workspaces", "user-interaction"),
	openProject: () => googleMetricEvent("open-project", "user-interaction"),
	shareProject: (isShared: boolean) =>
		googleMetricEvent("share-project", "user-interaction", { isShared }),
	openShareProjectModal: () => googleMetricEvent("open-share-project-modal", "user-interaction"),

	useTemplate: (templateName: string, group?: string, type?: string) =>
		googleMetricEvent("use-template", "user-interaction", {
			templateName: templateName,
			...(group ? { group } : {}),
			...(type ? { type } : {})
		}),
	artisticModeSelect: (value: string | boolean) =>
		googleMetricEvent("artistic-mode", "user-interaction", {
			value
		}),
	firstStyleClick: (styleName: string) =>
		googleMetricEvent("first-style-click", "user-interaction", {
			styleName
		}),
	styleClick: (styleName: string) =>
		googleMetricEvent("style-click", "user-interaction", {
			styleName
		}),
	styleInfoCLick: (styleName: string) =>
		googleMetricEvent("style-info-click", "user-interaction", {
			styleName
		}),
	styleGenerateCLick: (styleName: string, isPremium: boolean) =>
		googleMetricEvent("style-generate-click", "user-interaction", {
			styleName,
			isPremium
		}),
	authSubmitEvent: (formId: string) =>
		googleMetricEvent("auth-submit-event", "user-interaction", {
			formId
		}),

	authSignUp: () => googleMetricEvent("auth-event-sign-up", "user-interaction"),

	authSetActiveForm: (formName: string) =>
		googleMetricEvent("auth-set-active-form", "user-interaction", {
			formName
		}),
	auth0authClick: (providerName: string) =>
		googleMetricEvent("auth-0-auth-click", "user-interaction", {
			providerName
		}),
	authFormFieldChanged: (formName: string, fieldName: string) =>
		googleMetricEvent("auth-form-field-change", "user-interaction", {
			formName,
			fieldName
		}),
	anyAuthFormFieldChanging: (formName: string, fieldName: string) =>
		googleMetricEvent("any-auth-form-field-changing", "user-interaction", {
			formName,
			fieldName
		}),
	authFirstVisit: (page: string) =>
		googleMetricEvent("auth-first-visit", "user-interaction", {
			page
		}),
	authVisit: (page: string) =>
		googleMetricEvent("auth-visit", "user-interaction", {
			page
		}),
	undo: () => googleMetricEvent("undo", "user-interaction"),
	redo: () => googleMetricEvent("redo", "user-interaction"),

	/**
	 * historyEvents
	 */

	historyItemClick: () => googleMetricEvent("history-item-click", "user-interaction"),
	historyOpen: () => googleMetricEvent("history-open", "user-interaction"),
	historySetFavorite: (isFavorite: boolean, taskId: number) =>
		googleMetricEvent("history-set-favorite", "user-interaction", { isFavorite, taskId }),
	historyPremiumDisabledClick: () =>
		googleMetricEvent("history-premium-disabled-click", "user-interaction"),
	historySwitchView: (isGrouped: boolean) =>
		googleMetricEvent("history-switch-view", "user-interaction", {
			isGrouped
		}),
	historyCopyPrompt: () => googleMetricEvent("history-copy-prompt", "user-interaction"),
	historySaveAsset: () => googleMetricEvent("history-save-asset", "user-interaction"),
	historyApplyFilters: (filters: Record<string | number, string | number | boolean>) =>
		googleMetricEvent("history-apply-filters", "user-interaction", filters),

	isOnBoardingInitSkipped: () => googleMetricEvent("redo", "is-onboarding-skipped"),

	align: () => googleMetricEvent("node-align", "user-interaction"),

	wikiLinkClick: () => googleMetricEvent("wiki-link-clock", "user-interaction"),
	homeLinkClick: () => googleMetricEvent("home-link-clock", "user-interaction"),

	collapseSideBar: (isCollapsed: boolean) =>
		googleMetricEvent("collapse-side-bar", "user-interaction", {
			isCollapsed: isCollapsed
		}),
	openLibrary: () => googleMetricEvent("open-library", "user-interaction"),
	closeLibrary: () => googleMetricEvent("close-library", "user-interaction"),

	openContextMenu: () => googleMetricEvent("open-context-menu", "user-interaction"),
	/**
	 * connection events
	 */
	tryAddConnection: (fromGlobalId: string) =>
		googleMetricEvent("try-add-connection", "user-interaction", {
			fromGlobalId
		}),
	addConnection: (fromGlobalId: string, toGlobalId: string) =>
		googleMetricEvent("add-connection", "user-interaction", {
			fromGlobalId,
			toGlobalId
		}),
	deleteConnection: (fromGlobalId: string, toGlobalId: string) =>
		googleMetricEvent("delete-connection", "user-interaction", {
			fromGlobalId,
			toGlobalId
		}),

	changeInputValue: (nodeGlobalId: string, inputName: string) =>
		googleMetricEvent("change-input-value", "user-interaction", {
			nodeGlobalId,
			inputName
		}),
	changeParamValue: (nodeGlobalId: string, inputName: string) =>
		googleMetricEvent("change-param-value", "user-interaction", {
			nodeGlobalId,
			inputName
		}),
	fitToScreen: () => googleMetricEvent("fit-to-screen", "user-interaction"),
	getFileLink: (timeToFetch: number, fileId: number) =>
		googleMetricEvent("get-file-link", "user-interaction", {
			timeToFetch,
			fileId
		}),
	promocodeApplied: (promocode: string) =>
		googleMetricEvent("promocode_applied", "user-interaction", {
			promocode
		}),
	addNodeViaOnBoardingFeed: () =>
		googleMetricEvent("add-node-via-onboarding", "user-interaction"),
	onboardingLinkClick: (templateId?: number) =>
		googleMetricEvent("onboarding-link-click", "user-interaction", {
			...(templateId ? { templateId } : {})
		}),
	downloadFile: (timeToFetch: number, fileSize: number, fileId: number) =>
		googleMetricEvent("download-file", "user-interaction", {
			timeToFetch,
			fileSize,
			fileId
		}),

	/*
	 * node execution events
	 */
	executeNode: (globalId: string, executionTs: number) =>
		googleMetricEvent("execute-node", "user-interaction", {
			globalId,
			executionTs
		}),
	cancelTask: (globalId: string, taskId: number, executionTsDelta: number) =>
		googleMetricEvent("cancel-task", "user-interaction", {
			globalId,
			taskId,
			executionTsDelta
		}),
	createTask: (
		globalId: string,
		executionTs: number,
		taskId: number | string,
		executionTsDelta: number,
		params: Record<string, any> = {}
	) =>
		googleMetricEvent("create-task-front", "user-interaction", {
			globalId,
			executionTs,
			taskId,
			executionTsDelta,
			...getWorkspaceEventParams(),
			...params
		}),
	firstPendingTaskStatus: (
		globalId: string,
		_executionTs: number,
		taskId: number,
		position: number,
		executionTsDelta: number
	) =>
		googleMetricEvent("first-pending-task-status", "user-interaction", {
			globalId,
			taskId,
			executionTsDelta,
			...(position !== undefined ? { position } : {})
		}),
	executeNodeSuccess: (
		globalId: string,
		executionTs: number,
		taskId: number | string,
		executionTsDelta: number
	) =>
		googleMetricEvent("execute-node-success", "success", {
			globalId,
			executionTs,
			taskId,
			executionTsDelta
		}),
	executeNodeError: (
		globalId: string,
		errorMessage: string,
		executionTs?: number,
		taskId?: number | string,
		executionTsDelta?: number
	) =>
		googleMetricEvent("execute-node-error", "error", {
			globalId,
			errorMessage,
			executionTs,
			...(taskId ? { taskId } : {}),
			executionTsDelta
		}),
	giftSeasonBtnClick: () => googleMetricEvent("gift-season-btn-click", "user-interaction"),
	clearNode: () => googleMetricEvent("clear-node", "user-interaction"),
	deleteNode: (globalId: string) =>
		googleMetricEvent("delete-node", "user-interaction", { globalId }),
	tryReloadPage: () => googleMetricEvent("try-reload-page", "user-interaction"),
	promptHelper: (eventType: string) =>
		googleMetricEvent("prompt-helper", "user-interaction", { eventType }),
	switchSidebarMenu: (optionName: string) =>
		googleMetricEvent("switch-sidebar-menu", "user-interaction", {
			selectedName: optionName
		}),

	genDisabledBtnClick: (nodeGlobalId: string) =>
		googleMetricEvent("gen-disabled-btn-click", "user-interaction", {
			nodeGlobalId
		}),
	premiumNodeBanner: (nodeGlobalId: string) =>
		googleMetricEvent("premium-node-banner", "user-interaction", {
			nodeGlobalId
		}),
	premiumTemplateClick: (type: string, name: string) =>
		googleMetricEvent("premium-node-banner", "user-interaction", {
			type,
			name
		}),
	/**
	 *
	 * @description recap events
	 */
	shareRecapBtnClick: () => googleMetricEvent("share-recap-btn-click", "user-interaction"),
	showRecap: () => googleMetricEvent("show-recap", "user-interaction"),
	moveRecap: (index: number) => googleMetricEvent("move-recap", "user-interaction", { index }),
	premiumParamClick: (paramName: string) =>
		googleMetricEvent("premium-param-click", "user-interaction", {
			paramName
		}),
	premiumIntParamButtonClick: (paramProps: Record<string, string | number>) =>
		googleMetricEvent("premium-int-param-btn-click", "user-interaction", paramProps),
	premiumPopUp: {
		switchPaymentMethod: (isRuCards: boolean) =>
			googleMetricEvent("switch-payment-method", "user-interaction", { isRuCards }),
		premiumBannerChangePeriod: (period: string) =>
			googleMetricEvent("premium-banner-change-period", "user-interaction", {
				period
			}),
		premiumBannerHover: (period: string, planId: string) =>
			googleMetricEvent("premium-banner-hover", "user-interaction", {
				period,
				planId
			})
	},

	bugBtnClick: () => googleMetricEvent("bug-btn-click", "user-interaction"),
	saveAsset: () => googleMetricEvent("save-asset", "user-interaction", getWorkspaceEventParams()),
	inpaintGenerate: () => googleMetricEvent("inpaint-generate", "user-interaction"),
	/**
	 * Unified hover feature analytics
	 */
	hoverUsed: (hover_id: string, source: "footer" | "header" | "context_menu" | (string & {})) =>
		googleMetricEvent("hover_used", "user-interaction", {
			hover_id,
			source
		}),
	hoverMenuInteraction: (interactionName: string, isDisabled = false) =>
		googleMetricEvent("hover-menu-interaction", "user-interaction", {
			interactionName,
			isDisabled
		}),
	saveWorkspace: () => googleMetricEvent("save-workspace", "user-interaction"),
	saveWorkspaceBtnClick: () => googleMetricEvent("save-workspace-btn-click", "user-interaction"),

	trainNewModelBtnClick: () => googleMetricEvent("train-new-model-btn-click", "user-interaction"),
	openTutorials: () => googleMetricEvent("open-tutorials", "user-interaction"),
	openFaq: () => googleMetricEvent("open-faq", "user-interaction"),
	openDocumentation: () => googleMetricEvent("open-documentation", "user-interaction"),
	openRequestOnboarding: () => googleMetricEvent("open-request-onboarding", "user-interaction"),
	supportLinkClick: () => googleMetricEvent("support-link-click", "user-interaction"),
	releasesLinkClick: () => googleMetricEvent("releases-link-click", "user-interaction"),
	saveModelFromTrainPanel: () =>
		googleMetricEvent("save-model-from-train-panel", "user-interaction"),

	/**
	 * quick tout events
	 */
	skipQuickTutor: (isSkipped: boolean) =>
		googleMetricEvent("skip-quick-tutor", "user-interaction", {
			isSkipped
		}),
	quickTutor: () => googleMetricEvent("open-quick-tutor", "user-interaction"),
	closeQuickTutorModal: () => googleMetricEvent("close-quick-tutor-modal", "user-interaction"),
	startWidthoutQuickTour: () => googleMetricEvent("start-without-quick-tour", "user-interaction"),
	quickTourDiscordBtnClick: () =>
		googleMetricEvent("quick-tour-discord-btn-click", "user-interaction"),
	runQuickTour: (source: string) =>
		googleMetricEvent("run-quic-tourk", "user-interaction", { source }),
	moveToNextStepWidget: (step: number) =>
		googleMetricEvent("move-quick-tour-next-widget-step", "user-interaction", { step }),
	moveToPrevStepWidget: (step: number) =>
		googleMetricEvent("move-quick-tour-widget-prev-step", "user-interaction", { step }),
	skipQuickTourWidget: (step: number) =>
		googleMetricEvent("skip-quick-tour-widget", "user-interaction", { step }),
	finishQuickTourWidget: () => googleMetricEvent("finish-quick-tour-widget", "user-interaction"),

	stepTutorModal: {
		open: (id: string) => googleMetricEvent("tutor-modal-open", "user-interaction", { id }),
		close: (id: string) => googleMetricEvent("tutor-modal-close", "user-interaction", { id }),
		next: (id: string, index: number) =>
			googleMetricEvent("tutor-modal-next", "user-interaction", { id, index }),
		prev: (id: string, index: number) =>
			googleMetricEvent("tutor-modal-prev", "user-interaction", { id, index })
	},

	trainPanelLinkClick: () => googleMetricEvent("train-panel-link", "user-interaction"),
	openMlLibrary: () => googleMetricEvent("open-ml-library", "user-interaction"),

	premiumBannerOpenClose: (isOpen: boolean, source?: string) =>
		googleMetricEvent("premium-banner-open-close", "user-interaction", {
			isOpen,
			...(source ? { source } : {})
		}),

	offerModalOpenClose: (isOpen: boolean, promoCode: string) =>
		googleMetricEvent("offer-banner-open-close", "user-interaction", {
			isOpen,
			promoCode
		}),
	offerModalUpgrade: (promoCode: string) =>
		googleMetricEvent("offer-banner-upgrade", "user-interaction", { promoCode }),
	offerModalCopy: (promoCode: string) =>
		googleMetricEvent("offer-banner-copy", "user-interaction", { promoCode }),
	getIt: (globalId: string) =>
		googleMetricEvent("get-it-btn-click", "user-interaction", { globalId }),
	openNodeInstruction: (globalId: string) =>
		googleMetricEvent("open-node-instruction", "user-interaction", { globalId }),
	askAi: (globalId: string, errorMessage?: string) =>
		googleMetricEvent("open-node-instruction", "user-interaction", {
			globalId,
			...(errorMessage ? { errorMessage } : {})
		}),

	getPremiumBtnClick: () => googleMetricEvent("get-premium-btn-click", "user-interaction"),

	premiumBannerScrollTarget: (targetName: string) =>
		googleMetricEvent("premium-banner-scroll-target", "user-interaction", { targetName }),

	premiumBannerBtnClick: (
		mode: string,
		period: string,
		planId: string,
		sourceGlobalId?: string,
		isTrial?: boolean,
		source?: string
	) =>
		googleMetricEvent("premium-banner-btn-click", "user-interaction", {
			mode,
			period,
			planId,
			isTrial,
			...(sourceGlobalId ? { sourceGlobalId } : {}),
			...(source ? { source } : {})
		}),
	firstWorkspaceVisit: () => googleMetricEvent("first-workspace-visit", "user-interaction"),

	verifyEmailEvent: () => googleMetricEvent("verify-email-event", "user-interaction"),
	useMyDiscountBtnClick: () => googleMetricEvent("use-my-discount-btn-click", "user-interaction"),
	paymentSucceeded: (plan_id: string) =>
		googleMetricEvent("payment_succeeded", "user-interaction", { plan_id }),
	paymentFailed: (error_code: string, plan_id: string) =>
		googleMetricEvent("payment_failed", "user-interaction", {
			error_code,
			plan_id
		}),
	// Legacy event for backward compatibility
	purchace: (value: number, transaction_id: string, coupon?: string) =>
		googleMetricEvent("purchase", "user-interaction", {
			value,
			transaction_id,
			currency: "USD",
			...(coupon ? { coupon } : {})
		}),
	getCheckoutLink: (tsDiff: number) =>
		googleMetricEvent("get-checkout-link", "user-interaction", {
			tsDiff
		}),
	sendErrorInfo: async (name: string, params: errorParams, isNeedToSendToAnalitics = true) => {
		GrayLog.sendErrorLog({
			...params,
			log_name: name,
			isNeedToSendToAnalitics
		} as sendErrorLogProps);
	},
	performanceEvent: (eventName: string, params: Record<string, number | string>) =>
		googleMetricEvent(eventName, "performance", params),

	/**
	 *
	 * @description magic canvas events
	 */
	mcChangeFill: () => googleMetricEvent("mc-change-fill", "user-interaction"),

	mcImport: () => googleMetricEvent("mc-import-file", "user-interaction"),
	mcPickInstrument: (instrumentId: string) =>
		googleMetricEvent("mc-pick-instrument", "user-interaction", { instrumentId }),
	mcPickImage: (fileId: number, imgLink: string) =>
		googleMetricEvent("mc-pick-image", "user-interaction", { fileId, imgLink }),
	expandCollapsePricingTable: (tableId: string, isExpanded: boolean) =>
		googleMetricEvent("expand-collapse-pricing-table", "user-interaction", {
			tableId,
			isExpanded
		}),
	pricing: {
		bookADemo: () => googleMetricEvent("pricing-book-a-demo", "user-interaction"),
		addPromoCode: () => googleMetricEvent("pricing-add-promo-code", "user-interaction")
	},
	styles: {
		open: (globalId: string) =>
			googleMetricEvent("styles-open", "user-interaction", {
				globalId
			}),
		close: (globalId: string) =>
			googleMetricEvent("styles-close", "user-interaction", {
				globalId
			}),
		selectStyle: (globalId: string, styleId: string) =>
			googleMetricEvent("styles-select", "user-interaction", {
				globalId,
				styleId
			}),
		applyStyle: (globalId: string, styleId: string) =>
			googleMetricEvent("styles-apply", "user-interaction", {
				globalId,
				styleId
			}),
		useWithout: (globalId: string) =>
			googleMetricEvent("styles-use-without", "user-interaction", {
				globalId
			})
	},
	/**
	 * Universal subscription update event
	 */
	subscriptionUiInteracted: (
		action: SubscriptionAction | (string & {}),
		account_type?: AccountType,
		source?: SubscriptionSource | (string & {})
	) =>
		googleMetricEvent("subscription_ui_interacted", "user-interaction", {
			action,
			...(account_type && { account_type }),
			...(source && { source })
		}),
	/**
	 * Universal subscription update event
	 */
	accountUiInteracted: (action: string, source?: string) =>
		googleMetricEvent("account_interacted", "user-interaction", {
			action,
			...(source && { source })
		}),

	/**
	 * events on unsubscribe user flow (legacy events for backward compatibility)
	 */
	unsubscribe: {
		/**
		 * user press keep subscription on benifits stage
		 * @fires unsubscribe-keep-benifits
		 */
		keepBenifits: () => googleMetricEvent("unsubscribe-keep-benifits", "user-interaction"),
		/**
		 * user press continue unsubscribe on benifits stage
			@fires unsubscribe-continue-benifits
		 */
		continueBenifits: () =>
			googleMetricEvent("unsubscribe-continue-benifits", "user-interaction"),
		/**
		 * user press continue to downgrade on filled questionary stage
		 * @fires unsubscribe-submit-downgrade
		 */
		submitDowngrade: () =>
			googleMetricEvent("unsubscribe-submit-downgrade", "user-interaction"),
		accountHeaderBtnClick: () =>
			googleMetricEvent("account-header-btn-click", "user-interaction"),
		manageSubscriptionBtnClick: (mode: string) =>
			googleMetricEvent("manage-subscription-btn-click", "user-interaction", { mode }),
		unsubscribeQuestionsParamChange: (paramName: string, paramValue: string) =>
			googleMetricEvent("unsubscribe-questions-param-change", "user-interaction", {
				paramName,
				paramValue
			}),
		backToPlansBtnClick: () => googleMetricEvent("back-to-plans-btn-click", "user-interaction"),
		goToStripeLinkClick: () => googleMetricEvent("go-to-stripe-link-click", "user-interaction")
	},
	setupAccount: {
		moveToNextStep: (stepId: number, values: Record<string, any>) =>
			googleMetricEvent("setup-account-move-next", "user-interaction", {
				stepId,
				...addPrefixToKeys(values, "field_value_")
			}),
		moveToPrevStep: (stepId: number) =>
			googleMetricEvent("setup-account-move-prev", "user-interaction", { stepId }),
		openCloseDropDrown: (isOpen: boolean, fieldId: string) =>
			googleMetricEvent("setup-account-drop-down-open-close", "user-interaction", {
				isOpen,
				fieldId
			}),
		setupAccountActiveStage: (stage: number) =>
			googleMetricEvent("setup-account-active-stage", "user-interaction", { stage }),
		setupAccountChangeField: (fieldId: string) =>
			googleMetricEvent("setup-account-field-change", "user-interaction", { fieldId })
	},
	/**
	 * @description Events related to GPT Assistant interactions.
	 */
	gptEvents: {
		/**
		 * @description Tracks when the GPT popover becomes visible to the user.
		 * @fires gpt-popover-shown
		 */
		popoverShown: () => googleMetricEvent("gpt-popover-shown", "user-interaction"),
		/**
		 * @description Tracks when the GPT popover is closed by the user.
		 * @fires gpt-popover-closed
		 */
		popoverClosed: () => googleMetricEvent("gpt-popover-closed", "user-interaction"),
		/**
		 * @description Tracks when the user toggles GPT popover fullscreen mode.
		 * @param {boolean} isFullscreen - True if the popover is in fullscreen mode, false otherwise.
		 * @fires gpt-fullscreen-toggled
		 */
		fullscreenToggled: (isFullscreen: boolean) =>
			googleMetricEvent("gpt-fullscreen-toggled", "user-interaction", { isFullscreen }),
		/**
		 * @description Tracks when a user presses a suggestion hint in a GPT placeholder.
		 * @param {string} mode - The current GPT mode (e.g., 'dialog', 'assistant', 'prompt-generation').
		 * @param {string} hintText - The text of the hint pressed by the user.
		 * @fires gpt-hint-pressed
		 */
		hintPressed: (mode: string, hintText: string) =>
			googleMetricEvent("gpt-hint-pressed", "user-interaction", { mode, hintText }),
		/**
		 * @description Tracks when a user presses a premium button within a GPT placeholder.
		 * @param {string} mode - The GPT mode or source identifier (e.g., 'gpt', 'prompt') associated with the placeholder where the premium button was clicked.
		 * @fires gpt-premium-placeholder-button-clicked
		 */
		premiumPlaceholderButtonClicked: (mode: string) =>
			googleMetricEvent("gpt-premium-placeholder-button-clicked", "user-interaction", {
				mode
			}),
		/**
		 * @description Tracks when the GPT Assistant's visibility is toggled (opened/closed).
		 * This event corresponds to the original 'open-collapse-chat-gpt' Google Analytics event.
		 * @param {boolean} isOpen - True if the GPT Assistant is opened, false if closed.
		 * @fires open-collapse-chat-gpt
		 */
		toggledVisibility: (isOpen: boolean) =>
			googleMetricEvent("open-collapse-chat-gpt", "user-interaction", {
				isOpen
			}),
		/**
		 * @description Tracks when the user switches the GPT mode (e.g., 'dialog', 'assistant').
		 * This event corresponds to the original 'chat-gpt-mode' Google Analytics event.
		 * @param {string} mode - The new GPT mode selected by the user.
		 * @fires chat-gpt-mode
		 */
		modeSwitched: (mode: string) =>
			googleMetricEvent("chat-gpt-mode", "user-interaction", {
				mode
			}),
		/**
		 * @description Tracks when a response is generated by the GPT Assistant.
		 * This event corresponds to the original 'generate-chat-gpt-answer' Google Analytics event.
		 * @param {string} mode - The GPT mode in which the answer was generated.
		 * @fires generate-chat-gpt-answer
		 */
		generateChatGptAnswer: (mode: string, eventSource: string) =>
			googleMetricEvent("generate-chat-gpt-answer", "user-interaction", {
				chatGptMode: mode,
				eventSource
			}),
		/**
		 * @description Tracks when the user copies a prompt from the GPT Assistant.
		 * This event corresponds to the original 'copy-prompt-chat-gpt' Google Analytics event.
		 * @fires copy-prompt-chat-gpt
		 */
		copyPromptGpt: () => googleMetricEvent("copy-prompt-chat-gpt", "user-interaction"),
		/**
		 * @description Tracks when a node is added from the GPT Assistant.
		 * This event corresponds to the original 'add-node-chat-gpt' Google Analytics event.
		 * @fires add-node-chat-gpt
		 */
		nodeAdded: () => googleMetricEvent("add-node-chat-gpt", "user-interaction")
	},
	selectBurgetMenuItem: (props: { itemId: string }) =>
		googleMetricEvent("select-burget-menu-item", "user-interaction", props),
	selectHelpMenuItem: (props: { itemId: string }) =>
		googleMetricEvent("select-help-menu-item", "user-interaction", props),
	selectWorkspaceMenuItem: (props: { itemId: string }) =>
		googleMetricEvent("select-workspace-menu-item", "user-interaction", props),
	bookDemoEvents: {
		/**
		 * show book demo pop over
		 */
		showBookDemoPopOver: () => googleMetricEvent("book-demo-show-pop-over", "user-interaction"),
		/**
		 * user clicks book demo btn in headaer
		 */
		headerBookBtnClick: () =>
			googleMetricEvent("book-demo-header-btn-click", "user-interaction"),
		/**
		 * user clicks book demo btn in pop over
		 */
		popOverBookBtnClick: () =>
			googleMetricEvent("book-demo-pop-over-btn-click", "user-interaction"),
		/**
		 * user clicks book demo btn in banner
		 */
		bannerBookBtnClick: () =>
			googleMetricEvent("book-demo-banner-btn-click", "user-interaction"),
		/**
		 * user closes book banner
		 */
		bannerCloseBtnClick: () =>
			googleMetricEvent("book-demo-close-banner-btn-click", "user-interaction"),
		/**
		 * user closes book pop over
		 */
		popOverCloseBtnClick: () =>
			googleMetricEvent("book-demo-close-pop-over-btn-click", "user-interaction")
	},

	newYearEvents: {
		/**
		 * show new year limit banner when youser hit the limit
		 */
		showLimitBanner: () => googleMetricEvent("new-year-show-limit-banner", "user-interaction"),
		/**
		 * user clicks grab it in limit banner
		 */
		limitBannerOpenBanner: () =>
			googleMetricEvent("new-year-open-banner-from-limit", "user-interaction"),
		/**
		 * user clicks new year header line
		 */
		movingLineClick: () => googleMetricEvent("new-year-moving-line-click", "user-interaction"),
		/**
		 * user copies promocode from new year banner
		 */
		bannerCopyPromocode: () =>
			googleMetricEvent("new-year-banner-copy-promocode", "user-interaction"),
		/**
		 * user open tariffs from new year banner
		 */
		bannerOpentariffs: () =>
			googleMetricEvent("new-year-banner-open-tariffs", "user-interaction"),
		/**
		 * user copies promoce from tariffs banner
		 */
		tariffsCopyPromoCode: () =>
			googleMetricEvent("new-year-tariffs-copy-code", "user-interaction")
	},
	personalizedTemplates: {
		/**
		 * personalized templates modal open
		 */
		mountPersonalizedTemplatesModal: (source: string) =>
			googleMetricEvent("mount-personalized-templates", "user-interaction", { source }),
		/**
		 * user closes personalized templates
		 */
		closePersonalizedTemplates: () =>
			googleMetricEvent("close-personalized-templates", "user-interaction"),
		/**
		 * user clicks on personalized template card
		 */
		personalizedTemplateCardClick: (
			templateId: number,
			fromCategory: string,
			fromSection: number,
			openedFrom: string
		) =>
			googleMetricEvent("personalized-template-card-click", "user-interaction", {
				templateId,
				fromCategory,
				fromSection,
				openedFrom
			}),
		/**
		 * user change active category
		 */
		personalizedTemplateChangeCategory: (
			category: string,
			section: number,
			openedFrom: string
		) =>
			googleMetricEvent("personalized-template-change-category", "user-interaction", {
				category,
				section,
				openedFrom
			}),
		/**
		 * user opens personalized template modal from sidebar
		 */
		personalizedTemplatesOpenFromSidebar: () =>
			googleMetricEvent("personalized-templates-open-sidebar", "user-interaction"),
		/**
		 * track template list loaded time for user
		 */
		personalizedTemplatesLoaded: (category: string, diff: number) =>
			googleMetricEvent("personalized-templates-loaded", "user-interaction", {
				category,
				timeSpend: diff
			}),
		/**
		 * track template list images loaded time for user
		 */
		personalizedTemplatesLoadedImages: (category: string, diff: number) =>
			googleMetricEvent("personalized-templates-loaded-images", "user-interaction", {
				category,
				timeSpend: diff
			})
	},
	fileImport: {
		/**
		 * User opens file picker via button click
		 */
		openFilePicker: (source: "button" | "init-state") =>
			googleMetricEvent("file-import-open-picker", "user-interaction", { source }),

		/**
		 * User drops files into drop zone
		 */
		dropFiles: () => googleMetricEvent("file-import-drop-files", "user-interaction")
	},
	/**
	 * Track when model upload starts
	 */
	uploadModelStart: () => googleMetricEvent("upload-model-start", "user-interaction"),
	disabledPromptHelperExtend: () =>
		googleMetricEvent("disabled-prompt-helper-extend", "user-interaction"),

	checkoutInitiated: (plan_type: string) =>
		googleMetricEvent("checkout_initiated", "user-interaction", {
			plan_type
		}),

	checkoutCompleted: (plan_type: string) =>
		googleMetricEvent("checkout_completed", "user-interaction", {
			plan_type
		}),

	creditsRequested: (account_type: AccountType, action: string) =>
		googleMetricEvent("credits_requested", "user-interaction", {
			account_type,
			action
		}),

	/**
	 * Team events
	 */
	team: {
		/**
		 * Create Team Modal events
		 */
		createTeam: {
			modalOpened: () =>
				googleMetricEvent("create-team-modal-opened", "user-interaction", {}),
			modalClosed: () =>
				googleMetricEvent("create-team-modal-closed", "user-interaction", {}),
			nameInput: () => googleMetricEvent("create-team-name-input", "user-interaction", {}),
			seatsDecrease: () =>
				googleMetricEvent("create-team-seats-decrease-click", "user-interaction", {}),
			seatsIncrease: () =>
				googleMetricEvent("create-team-seats-increase-click", "user-interaction", {}),
			seatsChanged: (seats_count: number) =>
				googleMetricEvent("create-team-seats-changed", "user-interaction", { seats_count }),
			yearlyPlanSelected: () =>
				googleMetricEvent("create-team-yearly-plan-selected", "user-interaction", {}),
			monthlyPlanSelected: () =>
				googleMetricEvent("create-team-monthly-plan-selected", "user-interaction", {}),
			paymentMethodClick: () =>
				googleMetricEvent("create-team-payment-method-click", "user-interaction", {}),
			paymentMethodSelected: (payment_method: string) =>
				googleMetricEvent("create-team-payment-method-selected", "user-interaction", {
					payment_method
				}),
			cancel: () => googleMetricEvent("create-team-cancel", "user-interaction", {}),
			confirm: (params: {
				team_name: string;
				seats_count: number;
				billing_plan: string;
				total_amount: number;
			}) => googleMetricEvent("create-team-confirm", "user-interaction", params),
			seatsLimitReached: (seats_count: number) =>
				googleMetricEvent("create-team-seats_limit_reached", "user-interaction", {
					seats_count
				}),
			contactManagerClick: (seats_count: number) =>
				googleMetricEvent("create-team-contact-manager-click", "user-interaction", {
					seats_count
				}),
			contactSalesClick: () =>
				googleMetricEvent("create-team-contact-sales-click", "user-interaction", {})
		},

		/**
		 * Payment Checking Modal events
		 */
		paymentChecking: {
			shown: (source: string) =>
				googleMetricEvent("payment-checking-shown", "user-interaction", { source }),
			closed: (source: string) =>
				googleMetricEvent("payment-checking-closed", "user-interaction", { source })
		},

		/**
		 * Payment Failed Modal events
		 */
		paymentFailed: {
			shown: (params: {
				source: string;
				team_name: string;
				seats_count: number;
				billing_plan: string;
				total_amount: number;
				error_code: string;
			}) => googleMetricEvent("payment-failed-shown", "user-interaction", params),
			contactUsClick: (params: {
				team_name: string;
				seats_count: number;
				billing_plan: string;
				total_amount: number;
			}) => googleMetricEvent("payment-failed-contact-us-click", "user-interaction", params),
			tryAgainClick: (params: {
				team_name: string;
				seats_count: number;
				billing_plan: string;
				total_amount: number;
			}) => googleMetricEvent("payment-failed-try-again-click", "user-interaction", params),
			modalClosed: () =>
				googleMetricEvent("payment-failed-modal-closed", "user-interaction", {})
		},

		/**
		 * Team Created Success Modal events
		 */
		teamCreatedSuccess: {
			shown: () => googleMetricEvent("team-created-success-shown", "user-interaction", {}),
			telegramInput: () =>
				googleMetricEvent("team-created-success-telegram-input", "user-interaction", {}),
			getCourse: () =>
				googleMetricEvent("team-created-success-get-course", "user-interaction", {}),
			close: () => googleMetricEvent("team-created-success-close", "user-interaction", {})
		},

		/**
		 * Invite Member events
		 */
		inviteMember: {
			inviteClick: () =>
				googleMetricEvent("team-account-invite-member-click", "user-interaction", {}),
			modalShown: () => googleMetricEvent("invite-member-shown", "user-interaction", {}),
			emailInput: () =>
				googleMetricEvent("invite-member-email-input", "user-interaction", {}),
			cancel: () => googleMetricEvent("invite-member-cancel-click", "user-interaction", {}),
			sendInvite: () =>
				googleMetricEvent("invite-member-send-invite-click", "user-interaction", {}),
			error: () => googleMetricEvent("invite-member-invite-error", "user-interaction", {}),
			pendingCancel: () =>
				googleMetricEvent(
					"team-account-invite-member-pending-cancel-click",
					"user-interaction",
					{}
				),
			copyLink: (invited_email: string) =>
				googleMetricEvent(
					"team-account-invite-member-copy-link-click",
					"user-interaction",
					{
						invited_email
					}
				),
			sentToastShown: (invited_email: string) =>
				googleMetricEvent("invite-member-sent-toast-shown", "user-interaction", {
					invited_email
				})
		},

		/**
		 * Delete Member events
		 */
		deleteMember: {
			removeClick: () =>
				googleMetricEvent(
					"team-account-invite-member-remove-click",
					"user-interaction",
					{}
				),
			modalShown: (member_email: string) =>
				googleMetricEvent("team-delete-member-shown", "user-interaction", {
					member_email
				}),
			cancel: (member_email: string) =>
				googleMetricEvent("team-delete-member-cancel-click", "user-interaction", {
					member_email
				}),
			confirm: (member_email: string) =>
				googleMetricEvent("team-delete-member-confirm-click", "user-interaction", {
					member_email
				}),
			success: (member_email: string) =>
				googleMetricEvent("team-delete-member-success", "user-interaction", {
					member_email
				}),
			error: (params: { member_email: string; error_message: string }) =>
				googleMetricEvent("team-delete-member-error", "user-interaction", params)
		},

		/**
		 * Add/Remove Seats Button events
		 */
		seatsButtons: {
			addSeatsClick: (params: { current_seats: number; active_members: number }) =>
				googleMetricEvent(
					"team-account-add-seats-button-click",
					"user-interaction",
					params
				),
			removeSeatsClick: (params: { current_seats: number; active_members: number }) =>
				googleMetricEvent(
					"team-account-remove-seats-button-click",
					"user-interaction",
					params
				)
		},

		/**
		 * Add Seats Modal events
		 */
		addSeats: {
			modalShown: (seats_count_current: number) =>
				googleMetricEvent("team-add-seats-modal-shown", "user-interaction", {
					seats_count_current
				}),
			decreaseClick: (seats_count_add: number) =>
				googleMetricEvent("team-add-seats-decrease_click", "user-interaction", {
					seats_count_add
				}),
			increaseClick: (seats_count_add: number) =>
				googleMetricEvent("team-add-seats-increase_click", "user-interaction", {
					seats_count_add
				}),
			countChanged: (params: {
				seats_count_current: number;
				seats_count_new: number;
				total_due: number;
				payment_method: string;
			}) => googleMetricEvent("team-add-seats-count-changed", "user-interaction", params),
			paymentMethodClick: () =>
				googleMetricEvent("team-add-seats-payment-method-click", "user-interaction", {}),
			cancel: () => googleMetricEvent("team-add-seats-cancel-click", "user-interaction", {}),
			confirm: (params: {
				seats_count_current: number;
				seats_count_new: number;
				total_due: number;
				payment_method: string;
			}) => googleMetricEvent("team-add-seats-confirm-click", "user-interaction", params),
			success: (params: { seats_added: number; new_total_seats: number }) =>
				googleMetricEvent("team-add-seats-success", "user-interaction", params),
			error: (params: {
				seats_count_current: number;
				seats_count_new: number;
				error_message: string;
			}) => googleMetricEvent("team-add-seats-error", "user-interaction", params)
		},

		/**
		 * Remove Seats Modal events
		 */
		removeSeats: {
			modalShown: (params: { seats_number_current: number; plan_price_current: number }) =>
				googleMetricEvent("team-remove-seats-shown", "user-interaction", params),
			memberToggle: (params: {
				member_account_id: number;
				is_checked: boolean;
				seats_number_current: number;
				seats_number_new: number;
				seats_to_remove: number;
				plan_price_current: number;
				plan_price_new: number;
				price_difference: number;
			}) => googleMetricEvent("team-remove-seats-member-toggle", "user-interaction", params),
			back: () => googleMetricEvent("team-remove-seats-back", "user-interaction", {}),
			close: () => googleMetricEvent("team-remove-seats-close", "user-interaction", {}),
			confirm: (params: {
				seats_number_current: number;
				seats_number_new: number;
				seats_to_remove: number;
				plan_price_current: number;
				plan_price_new: number;
				price_difference: number;
				update_at: string;
			}) => googleMetricEvent("team-remove-seats-confirm", "user-interaction", params),
			confirmLast: () =>
				googleMetricEvent("team-remove-seats-confirm-last", "user-interaction", {}),
			success: () => googleMetricEvent("team-remove-seats-success", "user-interaction", {}),
			error: (params: { seats_to_remove: number; error_message: string }) =>
				googleMetricEvent("team-remove-seats-error", "user-interaction", params)
		},

		/**
		 * Team Management events (with prefixed names)
		 */
		management: {
			inviteMemberClicked: () =>
				googleMetricEvent("team_management_invite_member_clicked", "user-interaction", {}),
			inviteSent: () =>
				googleMetricEvent("team_management_invite_sent", "user-interaction", {}),
			inviteLinkCopied: () =>
				googleMetricEvent("team_management_invite_link_copied", "user-interaction", {}),
			inviteCancelled: () =>
				googleMetricEvent("team_management_invite_cancelled", "user-interaction", {}),
			addMoreSeatsClicked: () =>
				googleMetricEvent("team_management_add_more_seats_clicked", "user-interaction", {}),
			seatsModalOpened: () =>
				googleMetricEvent("team_management_seats_modal_opened", "user-interaction", {}),
			seatsModalAdjusted: () =>
				googleMetricEvent("team_management_seats_modal_adjusted", "user-interaction", {}),
			seatsModalConfirmed: () =>
				googleMetricEvent("team_management_seats_modal_confirmed", "user-interaction", {}),
			seatsLimitModalOpened: () =>
				googleMetricEvent(
					"team_management_seats_limit_modal_opened",
					"user-interaction",
					{}
				),
			contactSalesClicked: () =>
				googleMetricEvent("team_management_contact_sales_clicked", "user-interaction", {}),
			memberRemoveClicked: () =>
				googleMetricEvent("team_management_member_remove_clicked", "user-interaction", {})
		},

		/**
		 * Team Subscription & Credits Page events
		 */
		subscription: {
			pageView: (params: {
				team_id: number;
				members_count: number;
				total_credits: number;
				credits_left: number;
			}) => googleMetricEvent("team-subscription-page-view", "user-interaction", params),
			reassignCreditsClick: () =>
				googleMetricEvent(
					"team_subscription_reassign-credits-btn_click",
					"user-interaction",
					{}
				),
			getCreditsClick: () =>
				googleMetricEvent(
					"team_subscription_get-credits-btn_click",
					"user-interaction",
					{}
				),
			contactClick: () =>
				googleMetricEvent("team_subscription_contact-btn_click", "user-interaction", {}),
			cancelPlanClick: () =>
				googleMetricEvent(
					"team_subscription_cancel-plan-btn_click",
					"user-interaction",
					{}
				),
			changePlanClick: () =>
				googleMetricEvent(
					"team_subscription_change-plan-btn_click",
					"user-interaction",
					{}
				),
			upgradeClick: () =>
				googleMetricEvent("team_subscription_upgrade-btn_click", "user-interaction", {}),
			reactivate: () =>
				googleMetricEvent("team-subscription-reactivate", "user-interaction", {})
		},

		/**
		 * Buy Credits (Team) events
		 */
		buyCredits: {
			reassignCreditsClick: (params: { user_email: string; team_name: string }) =>
				googleMetricEvent("team-reassign-credits-click", "user-interaction", params),
			buyCreditsClick: () =>
				googleMetricEvent("team-buy-credits-click", "user-interaction", {}),
			accountSelect: () =>
				googleMetricEvent("team-buy-credits-account-select", "user-interaction", {}),
			continueClick: () =>
				googleMetricEvent("team-buy-credits-continue-click", "user-interaction", {}),
			planSelected: () =>
				googleMetricEvent("team-buy-credits-plan-selected", "user-interaction", {})
		},

		/**
		 * Cancel Subscription (Team) events
		 */
		cancelSubscription: {
			cancelPlanClick: (params: {
				subscription_plan: string;
				subscription_status: string;
				team_subscription_plan: string;
				team_subscription_status: string;
				credits_left: number;
			}) => googleMetricEvent("team-account-cancel-plan-click", "user-interaction", params),
			modalShown: (params: {
				subscription_plan: string;
				subscription_status: string;
				team_subscription_plan: string;
				team_subscription_status: string;
				credits_left: number;
				stage: number;
			}) =>
				googleMetricEvent(
					"team-account-cancel-subscription-shown",
					"user-interaction",
					params
				),
			keepSubscription: (stage: number) =>
				googleMetricEvent("team-account-cancel-keepsubcription-click", "user-interaction", {
					stage
				}),
			continue: (params: { stage: number; selected_reasons: string[] }) =>
				googleMetricEvent("team-account-cancel-continue-click", "user-interaction", {
					stage: params.stage,
					selected_reasons: params.selected_reasons.join(", ")
				}),
			closed: (params: { stage: number; selected_reasons: string[] }) =>
				googleMetricEvent("team-account-cancel-subscription-closed", "user-interaction", {
					stage: params.stage,
					selected_reasons: params.selected_reasons.join(", ")
				}),
			back: (params: { stage: number; selected_reasons: string[] }) =>
				googleMetricEvent("team-account-cancel-back-click", "user-interaction", {
					stage: params.stage,
					selected_reasons: params.selected_reasons.join(", ")
				}),
			confirm: (params: { stage: number; selected_reasons: string[] }) =>
				googleMetricEvent("team-account-cancel-confirm", "user-interaction", {
					stage: params.stage,
					selected_reasons: params.selected_reasons.join(", ")
				}),
			reason: (params: { reason: string; is_checked: boolean }) =>
				googleMetricEvent("team-account-cancel-reason", "user-interaction", params),
			success: () => googleMetricEvent("team-account-cancel-success", "user-interaction", {}),
			error: (error_message: string) =>
				googleMetricEvent("team-account-cancel-error", "user-interaction", {
					error_message
				})
		},

		/**
		 * Credit Reassign events
		 */
		creditReassign: {
			previewLoaded: (params: {
				team_id: number;
				owner_user_id: string;
				recipients_count: number;
				total_amount: number;
				is_auto_distribute: boolean;
			}) => googleMetricEvent("credit_reassign_preview_loaded", "user-interaction", params),

			completed: (params: {
				team_id: number;
				owner_user_id: string;
				recipients_count: number;
				total_amount: number;
				is_auto_distribute: boolean;
				source_breakdown_type: string;
			}) => googleMetricEvent("credit_reassign_completed", "user-interaction", params),

			failed: (params: {
				team_id: number;
				owner_user_id: string;
				error_type: "insufficient_balance" | "validation_error" | "network_error";
				error_message: string;
			}) => googleMetricEvent("credit_reassign_failed", "user-interaction", params),

			cancelled: (params: {
				team_id: number;
				owner_user_id: string;
				stage: "selection" | "distribution" | "preview";
			}) => googleMetricEvent("credit_reassign_cancelled", "user-interaction", params),

			accessDenied: (params: {
				team_id: number;
				owner_user_id: string;
				reason: string;
				plan_name: string;
			}) => googleMetricEvent("credit_reassign_access_denied", "user-interaction", params)
		}
	},

	/**
	 * API Keys events (grouped under a single event name: "api-key")
	 */
	apiKeys: {
		/**
		 * Track API key creation
		 */
		createKey: (
			keyName: string,
			neverExpires: boolean,
			expiresAt?: string | null,
			source?: string
		) =>
			googleMetricEvent("api-key", "user-interaction", {
				action: "create",
				key_name: keyName,
				never_expires: neverExpires,
				...(expiresAt !== undefined ? { expires_at: expiresAt } : {}),
				...(source ? { source } : {})
			}),
		/**
		 * Track setting key to never expire (toggle on)
		 */
		setNeverExpires: (keyName: string, source?: string) =>
			googleMetricEvent("api-key", "user-interaction", {
				action: "set-never-expires",
				key_name: keyName,
				...(source ? { source } : {})
			}),
		/**
		 * Track API key expiration update
		 */
		updateKey: (keyName: string, neverExpires: boolean, expiresAt?: string | null) =>
			googleMetricEvent("api-key", "user-interaction", {
				action: "update",
				key_name: keyName,
				never_expires: neverExpires,
				...(expiresAt !== undefined ? { expires_at: expiresAt } : {})
			}),
		/**
		 * Track API key deletion
		 */
		deleteKey: (keyName: string) =>
			googleMetricEvent("api-key", "user-interaction", {
				action: "delete",
				key_name: keyName
			}),
		/**
		 * Track copying API key
		 */
		copyKey: (keyName: string, source?: string) =>
			googleMetricEvent("api-key", "user-interaction", {
				action: "copy",
				key_name: keyName,
				...(source ? { source } : {})
			})
	},

	/**
	 * Canvas events
	 */
	canvas: {
		switchAccountType: (params: { from: string; to: string }) =>
			googleMetricEvent("canvas-switch-account-type", "user-interaction", params),
		upgradeClick: (params?: { location?: string; account_type?: string }) =>
			googleMetricEvent("canvas-upgrade-click", "user-interaction", params || {}),
		createTeamClick: (params: { account_type: string }) =>
			googleMetricEvent("canvas-create-team-click", "user-interaction", params),
		openProfile: (params: { source: string; account_type: string }) =>
			googleMetricEvent("canvas-open-profile", "user-interaction", params),
		profileDropdownOpen: () =>
			googleMetricEvent("canvas-profile-dropdown-open", "user-interaction", {}),
		openProfileFromCredits: (params: {
			source: string;
			account_type: string;
			credits_left: number;
		}) => googleMetricEvent("canvas-open-profile-from-credits", "user-interaction", params),
		logoutClick: () => googleMetricEvent("canvas-logout-click", "user-interaction", {})
	},

	/**
	 * Account Overview events
	 */
	accountOverview: {
		pageView: (params: {
			account_type: string;
			plan: string;
			credits_left: number;
			credits_total: number;
		}) => googleMetricEvent("account-overview/page-view", "user-interaction", params),
		editProfileClick: () =>
			googleMetricEvent("account-edit-profile/click", "user-interaction", {}),
		sidebarTabClick: (params: { account_type: string; tab: string }) =>
			googleMetricEvent("account-sidebar-tab-click", "user-interaction", params),
		createTeamClick: (params: { plan: string }) =>
			googleMetricEvent("account-create-team/click", "user-interaction", params)
	},

	/**
	 * Personal Overview events
	 */
	personalOverview: {
		manageSubscriptionClick: () =>
			googleMetricEvent(
				"personal_overview_manage-subscription_click",
				"user-interaction",
				{}
			),
		getCreditsClick: (params: { account_type: string; plan: string }) =>
			googleMetricEvent(
				"personal_overview_get-credits-btn_click",
				"user-interaction",
				params
			),
		upgradeClick: (params: { account_type: string; plan: string }) =>
			googleMetricEvent("personal_overview_upgrade-btn_click", "user-interaction", params)
	},

	/**
	 * Personal Subscription events
	 */
	personalSubscription: {
		manageSubscriptionClick: () =>
			googleMetricEvent(
				"personal_subscription_manage-subscription_click",
				"user-interaction",
				{}
			),
		cancelPlanClick: () =>
			googleMetricEvent(
				"personal_subscription_cancel-plan-btn_click",
				"user-interaction",
				{}
			),
		changePlanClick: () =>
			googleMetricEvent(
				"personal_subscription_change-plan-btn_click",
				"user-interaction",
				{}
			),
		getCreditsClick: (params: { account_type: string; plan: string }) =>
			googleMetricEvent(
				"personal_subscription_get-credits-btn_click",
				"user-interaction",
				params
			),
		upgradeClick: (params: { account_type: string; plan: string }) =>
			googleMetricEvent("personal_subscription_upgrade-btn_click", "user-interaction", params)
	},

	/**
	 * Team Overview events
	 */
	teamOverview: {
		manageSubscriptionClick: () =>
			googleMetricEvent("team_overview_manage-subscription_click", "user-interaction", {}),
		cancelPlanClick: () =>
			googleMetricEvent("subscription-cancel-plan-click", "user-interaction", {}),
		changePlanClick: () =>
			googleMetricEvent("subscription-change-plan-click", "user-interaction", {}),
		getCreditsClick: (params: { account_type: string; plan: string }) =>
			googleMetricEvent("team_overview_get-credits-btn_click", "user-interaction", params),
		upgradeClick: (params: { account_type: string; plan: string }) =>
			googleMetricEvent("team_overview_upgrade-btn_click", "user-interaction", params)
	},

	/**
	 * Create Team Click events (from different sources)
	 */
	createTeamClick: {
		fromCanvas: () => googleMetricEvent("create-team-click-canvas", "user-interaction", {}),
		fromAccount: () => googleMetricEvent("create-team-click-account", "user-interaction", {}),
		fromPricing: () => googleMetricEvent("create-team-click-pricing", "user-interaction", {})
	}
};
export type EventAnalitycsType = typeof EventAnalytics;
export default EventAnalytics;
